import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { useMpRoom } from "../multiplayer/useMpRoom";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { GameView, type SpectatorApi } from "../game/GameView";
import { RaceStandings } from "./RaceStandings";
import { clearSession, setBattleSession } from "../local/session";
import {
  computeBonuses,
  rewardSettingsForLesson,
  type GameConfig,
  type RunOutcome,
} from "../game/types";
import { SEED_LESSONS, getSeedLesson } from "../data/seed-lessons";
import { recordRun } from "../local/profile";
import { loadGameSettings } from "../local/gameSettings";
import { applyLearnLanguage } from "../local/languageSwap";
import { getWordPack } from "../data/word-packs";
import { getTeachingLevel } from "../data/teaching-levels";
import { resolveQuizKinds } from "../data/quizStyles";
import type { SeedLesson } from "../data/seed-lessons";
import { sfx, speak } from "../game/audio";
import { SINGLE_COURSE_LENGTH } from "../game/courseConfig";
import { RaceLobby, type RaceEmbedProps } from "./RaceLobby";
import { RaceResults } from "./RaceResults";

function speedForLesson(orderIndex: number): number {
  return Math.min(22, 13 + Math.max(0, orderIndex - 1) * 0.7);
}

function placeText(place: number): string {
  if (place === 1) return "First place!";
  if (place === 2) return "Second place!";
  if (place === 3) return "Third place!";
  return `${place}th place`;
}

function placeLabel(place: number): string {
  if (place === 1) return "1st";
  if (place === 2) return "2nd";
  if (place === 3) return "3rd";
  return `${place}th`;
}

function playerLevel(coins: number): number {
  return 1 + Math.floor(coins / 200);
}

export function RacePlay({ codeOverride, identity, embedded }: RaceEmbedProps = {}) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { profile, setProfile } = useLocalProfile();
  const code = codeOverride ?? params.get("code");

  if (!embedded && !profile) {
    nav("/", { replace: true });
    return null;
  }
  const me = identity ?? {
    id: getBattlePlayerId(profile!.id, code),
    displayName: profile!.displayName,
    avatarConfig: profile!.avatarConfig,
  };

  const joinOpts = useMemo(() => (code ? { code, asHost: false, me } : null), [code, me.id]);
  const { client, state, positions } = useMpRoom(joinOpts);

  // Resolve the "lesson" from either form: a plain seed-lesson id, OR the new
  // pack:packId::L<n> form that the BattlePackPicker emits.
  const lesson: SeedLesson | null = useMemo(() => {
    if (!state) return null;
    const id = state.lessonId;
    const m = /^pack:(.+?)::L(\d+)$/.exec(id);
    if (m && m[1]) {
      const pack = getWordPack(m[1]);
      const tlevel = getTeachingLevel(Number(m[2]));
      if (pack && tlevel) {
        return {
          id,
          title: `${pack.title} • L${tlevel.index} ${tlevel.title}`,
          theme: pack.theme,
          timeOfDay: pack.timeOfDay,
          questionsCount: pack.words.length,
          objectsCount: 8,
          coinsCount: 40,
          isGlobal: true,
          orderIndex: tlevel.index,
          challengeKinds: tlevel.challengeKinds ?? null,
          words: pack.words,
        };
      }
    }
    return getSeedLesson(id) ?? null;
  }, [state?.lessonId]);
  const finishReportedRef = useRef(false);
  const spectatorRef = useRef<SpectatorApi | null>(null);
  const recordedRef = useRef(false);
  // Last-known own race s-distance — persisted across renders so a
  // defensive Game remount mid-race resumes at the right position instead
  // of snapping back to 0. Declared BEFORE the config memo so the memo's
  // `resumeFromS` read is in TDZ-safe scope.
  const lastSelfDistanceRef = useRef(0);
  const lastSentRef = useRef(0);
  const playersKey = useMemo(
    () =>
      state?.players
        .map((p) => `${p.id}|${p.slotIdx}|${p.displayName}|${JSON.stringify(p.avatarConfig)}`)
        .join("~") ?? "",
    [state?.players],
  );

  const config = useMemo<GameConfig | null>(() => {
    if (!lesson || !state) return null;
    const myPlayer = state.players.find((p) => p.id === me.id);
    const rewards = rewardSettingsForLesson(lesson.orderIndex, lesson.coinsCount);
    const settings = loadGameSettings();
    // Mirror single-player level shapes — level 1 is the Hit Parade course
    // (straight ramp, cannons, moving start platform, candy theme).
    const tlevel = getTeachingLevel(lesson.orderIndex);
    const courseMode = tlevel?.courseMode ?? "default";
    return {
      lessonId: lesson.id,
      words: applyLearnLanguage(lesson.words, profile?.learnLanguage),
      theme: lesson.theme,
      timeOfDay: lesson.timeOfDay,
      questionsCount: lesson.words.length,
      objectsCount: lesson.objectsCount,
      coinsCount: rewards.coinsCount,
      coinPickupValue: rewards.coinPickupValue,
      quizCorrectCoins: rewards.quizCorrectCoins,
      courseLength: 260,
      courseMode,
      // Server-broadcast countdownStartedAt is the SAME Date.now() value on
      // every client → moving platforms, cannons, and any other time-based
      // world animation tick in lockstep across devices.
      clockOrigin: state.countdownStartedAt,
      seed: state.seed,
      avatar: myPlayer?.avatarConfig ?? me.avatarConfig,
      challengeKinds: resolveQuizKinds(state.quizStyle, lesson.challengeKinds),
      upgrades: profile?.upgrades,
      runSpeed: speedForLesson(lesson.orderIndex),
      movementMode: settings.movementMode,
      movementSensitivity: settings.movementSensitivity,
      cameraSensitivity: settings.cameraSensitivity,
      controllerDeadzone: settings.controllerDeadzone,
      assistMode: settings.assistMode,
      remotePlayers: state.players
        .filter((p) => p.id !== me.id)
        .map((p) => ({
          id: p.id,
          displayName: p.displayName,
          avatarConfig: p.avatarConfig,
          slotIdx: p.slotIdx,
        })),
      mySlotIdx: myPlayer?.slotIdx ?? 0,
      spectateAfterDeath: true,
      // If the Game ever does get rebuilt mid-race (defensive — the deps
      // for GameView's mount effect should prevent this), resume from the
      // last s-distance we sent up rather than snapping back to 0.
      resumeFromS: lastSelfDistanceRef.current,
    };
  }, [
    lesson?.id,
    lesson,
    state?.seed,
    state?.quizStyle,
    playersKey,
    me.id,
    profile?.upgrades,
  ]);

  // (lastSelfDistanceRef + lastSentRef are declared earlier so the config
  //  memo can read them without hitting a TDZ.)
  const onMyTick = (
    s: number,
    laneOffset: number,
    runState: string,
    velocityS: number,
    velocityL: number,
    jumpSeq?: number,
    y?: number,
    vy?: number,
    grounded?: boolean,
  ) => {
    if (s > lastSelfDistanceRef.current) lastSelfDistanceRef.current = s;
    const now = performance.now();
    if (now - lastSentRef.current < 50) return; // ≥20 Hz
    lastSentRef.current = now;
    client?.sendTick({
      s,
      laneOffset,
      state: runState as "run" | "jump" | "hit" | "finished",
      velocityS,
      velocityL,
      jumpSeq,
      y,
      vy,
      grounded,
    });
  };

  const handleEnd = (o: RunOutcome) => {
    if (finishReportedRef.current) return;
    finishReportedRef.current = true;
    const bonus = computeBonuses(o);
    const correct = o.wordOutcomes.filter((w) => w.correct).length;
    client?.reportFinish({
      timeMs: o.timeMs,
      score: bonus.totalScore,
      correctCount: correct,
      totalQuestions: o.wordOutcomes.length,
    });
    // Persist coins / mastery / progress to the local profile. Skipped when
    // embedded in a class — the device profile is shared across students, so
    // recording a run there would mix up whoever's iPad this is.
    if (!embedded && lesson && profile && !recordedRef.current) {
      recordedRef.current = true;
      const result = recordRun(profile, {
        lessonId: lesson.id,
        score: bonus.totalScore,
        timeMs: o.timeMs,
        coinsEarned: bonus.totalCoins,
        hearts: o.hearts,
        wordOutcomes: o.wordOutcomes,
        finished: o.finished,
      });
      if (playerLevel(result.profile.coins) > playerLevel(profile.coins)) sfx.levelUp();
      setProfile(result.profile);
    }
  };

  useEffect(() => {
    if (state?.phase === "results" && !embedded) {
      clearSession();
      nav(`/battle/results?code=${state.code}`, { replace: true });
    }
  }, [state?.phase, nav, state?.code, embedded]);

  // Place announcement on local finish. My place is computed REACTIVELY from
  // the room state (so it stays correct as more players finish) with a tie
  // tolerance — two players finishing within TIE_MS of each other can't be
  // told apart over the network, so they share the place (both 1st, etc.).
  const TIE_MS = 280;
  const finishPlace = useMemo<number | null>(() => {
    if (!state) return null;
    const meRow = state.players.find((p) => p.id === me.id);
    if (!meRow?.finished || meRow.finishTimeMs == null) return null;
    const finished = state.players
      .filter((p) => p.finished && p.finishTimeMs != null)
      .sort((a, b) => (a.finishTimeMs ?? 0) - (b.finishTimeMs ?? 0));
    // Tie-bucket: a finisher within TIE_MS of the current bucket's first time
    // shares its place; otherwise a new bucket starts at rank index+1.
    let curPlace = 1;
    let bucketStart = finished[0]?.finishTimeMs ?? 0;
    const placeOf = new Map<string, number>();
    finished.forEach((p, i) => {
      if (i === 0) { placeOf.set(p.id, 1); return; }
      if ((p.finishTimeMs ?? 0) - bucketStart <= TIE_MS) {
        placeOf.set(p.id, curPlace);
      } else {
        curPlace = i + 1;
        bucketStart = p.finishTimeMs ?? 0;
        placeOf.set(p.id, curPlace);
      }
    });
    return placeOf.get(me.id) ?? 1;
  }, [state, me.id]);

  // The big centred banner shows for ~3.5s, then the place persists as a
  // small badge pinned to the top while the player spectates the rest of the
  // round (until the room moves to results).
  const [placeBig, setPlaceBig] = useState(false);
  const placeAnnouncedRef = useRef(false);
  useEffect(() => {
    if (finishPlace == null || placeAnnouncedRef.current) return;
    placeAnnouncedRef.current = true;
    setPlaceBig(true);
    speak(placeText(finishPlace));
    sfx.finish();
    const t = window.setTimeout(() => setPlaceBig(false), 3500);
    return () => window.clearTimeout(t);
  }, [finishPlace]);

  // Save the resume marker only while the race is actually running — never
  // for the lobby. This prevents a stale marker from auto-pulling the player
  // back into "create room" mode on next launch.
  useEffect(() => {
    if (!code || embedded) return;
    if (state?.phase === "racing" || state?.phase === "countdown") {
      setBattleSession(code, !!params.get("host"));
    }
  }, [state?.phase, code, params, embedded]);

  if (!code) {
    if (embedded) return null;
    nav("/battle", { replace: true });
    return null;
  }
  if (!state || !config) return <div className="full-center">Loading race…</div>;
  if (embedded && state.phase === "results") {
    return <RaceResults codeOverride={code} identity={identity} embedded />;
  }
  if (state.phase === "lobby") {
    // Embedded: render RaceLobby directly (same room connection) instead of
    // navigating — keeps the class connection alive.
    if (embedded) return <RaceLobby codeOverride={code} identity={identity} embedded asHost={state.hostId === me.id} />;
    // Preserve host flag so the lobby renders with host-only controls
    // (Start, Change level, etc.) when the host gets sent back via the
    // pause-overlay "Back to lobby" button.
    const hostQuery = state.hostId === me.id ? "&host=1" : "";
    nav(`/battle?code=${state.code}${hostQuery}`, { replace: true });
    return null;
  }

  const countdownLabel =
    (state.phase === "countdown" || state.phase === "racing") && state.countdownStartedAt
      ? (() => {
          const elapsed = Date.now() - state.countdownStartedAt;
          if (elapsed < 1000) return "3";
          if (elapsed < 2000) return "2";
          if (elapsed < 3000) return "1";
          if (elapsed < 3900) return "GO!";
          return null;
        })()
      : null;

  return (
    <>
      <GameView
        config={config}
        onEnd={handleEnd}
        positions={positions}
        onMyTick={onMyTick}
        paused={state.phase === "countdown" || !!state.hostPaused}
        countdownLabel={countdownLabel}
        spectatorRef={spectatorRef}
        isBattleHost={state.hostId === me.id}
        onBackToLobby={() => client?.backToLobby()}
        hostPaused={!!state.hostPaused}
        onHostPauseToggle={
          state.hostId === me.id
            ? (next) => client?.setHostPaused(next)
            : undefined
        }
      />
      <div
        style={{
          position: "fixed",
          top: 8,
          left: 8,
          padding: "6px 12px",
          background: "rgba(15,23,42,0.7)",
          color: "#fff",
          fontWeight: 900,
          letterSpacing: "0.18rem",
          borderRadius: 10,
          fontSize: "0.95rem",
          zIndex: 30,
          pointerEvents: "none",
        }}
        aria-label="Room code"
      >
        {state.code}
      </div>
      <SpectatorBar
        state={state}
        myId={me.id}
        spectatorRef={spectatorRef}
        finished={!!finishReportedRef.current}
      />
      {state.phase === "racing" && (
        <RaceStandings
          state={state}
          positions={positions}
          myId={me.id}
          courseLength={config.courseLength}
          myDistance={positions.get(me.id)?.s ?? 0}
        />
      )}
      {finishPlace != null && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            top: placeBig ? 0 : "max(env(safe-area-inset-top, 0px), 12px)",
            bottom: placeBig ? 0 : "auto",
            display: "flex",
            alignItems: placeBig ? "center" : "flex-start",
            justifyContent: "center",
            zIndex: 80,
            pointerEvents: "none",
            transition: "top 420ms cubic-bezier(.34,1.4,.64,1)",
          }}
          aria-live="polite"
        >
          <div
            style={{
              background: finishPlace === 1
                ? "linear-gradient(180deg, #fde047, #f59e0b)"
                : finishPlace === 2
                  ? "linear-gradient(180deg, #e2e8f0, #94a3b8)"
                  : finishPlace === 3
                    ? "linear-gradient(180deg, #fdba74, #c2410c)"
                    : "linear-gradient(180deg, #c4b5fd, #6d28d9)",
              color: "#1f2937",
              padding: placeBig ? "32px 60px" : "8px 22px",
              borderRadius: placeBig ? 36 : 999,
              fontSize: placeBig ? "5.2rem" : "1.5rem",
              fontWeight: 900,
              letterSpacing: placeBig ? "0.2rem" : "0.06rem",
              boxShadow: placeBig ? "0 24px 60px rgba(0,0,0,0.45)" : "0 8px 22px rgba(0,0,0,0.35)",
              textShadow: "0 3px 10px rgba(255,255,255,0.4)",
              animation: placeBig ? "placeIn 360ms cubic-bezier(.34,1.56,.64,1)" : undefined,
              transition: "padding 420ms ease, font-size 420ms ease, border-radius 420ms ease",
              display: "inline-flex",
              alignItems: "center",
              gap: placeBig ? 0 : 8,
            }}
          >
            {!placeBig && <span aria-hidden>🏁</span>}
            {placeLabel(finishPlace)}{placeBig ? "!" : ""}
          </div>
        </div>
      )}
    </>
  );
}

// Keep SEED_LESSONS imported so vite tree-shake doesn't drop it (used elsewhere).
void SEED_LESSONS;

/**
 * After-finish spectator overlay. Shows whom we're watching with prev/next
 * arrows to cycle through other still-racing players. Hidden while we're
 * still running ourselves and while waiting for the race to start.
 */
function SpectatorBar({
  state,
  myId,
  spectatorRef,
  finished,
}: {
  state: { players: { id: string; displayName: string; finished?: boolean }[]; phase: string };
  myId: string;
  spectatorRef: React.MutableRefObject<SpectatorApi | null>;
  finished: boolean;
}) {
  const [targetId, setTargetId] = useState<string | null>(null);
  // Poll the spectator target every 200ms — cheap, avoids prop plumbing.
  useEffect(() => {
    const id = window.setInterval(() => {
      setTargetId(spectatorRef.current?.targetId() ?? null);
    }, 200);
    return () => window.clearInterval(id);
  }, [spectatorRef]);

  if (!finished || state.phase !== "racing") return null;
  if (!spectatorRef.current?.isSpectating()) return null;

  const watched = state.players.find((p) => p.id === targetId);
  const stillRacing = state.players.filter((p) => !p.finished && p.id !== myId);
  if (stillRacing.length === 0) {
    return (
      <div className="spectator-bar" aria-hidden>
        <span className="spectator-bar-label">You finished! 🎉</span>
        <span className="spectator-bar-sub">Waiting for everyone else…</span>
      </div>
    );
  }
  const cycle = (dir: 1 | -1) => {
    const next = spectatorRef.current?.cycle(dir) ?? null;
    setTargetId(next);
  };
  return (
    <div className="spectator-bar" aria-hidden>
      <span className="spectator-bar-label">Watching</span>
      <div className="spectator-bar-controls">
        <button className="spectator-arrow" onClick={() => cycle(-1)} aria-label="Previous player">‹</button>
        <strong className="spectator-name">{watched?.displayName ?? "—"}</strong>
        <button className="spectator-arrow" onClick={() => cycle(1)} aria-label="Next player">›</button>
      </div>
      <span className="spectator-bar-sub">You finished! 🎉</span>
    </div>
  );
}
